<script>
import { updateFaviconAndTitle } from "../utils/helpers";
import AuthShell from "../components/auth/auth-shell.vue";

export default {
  name: "PortalLoginPage",
  components: {
    AuthShell,
  },
  data() {
    return {
      accountPublicId: this.$route.params.accountPublicId,
      accountInfo: null,
      loading: true,
      notAvailable: false,
      expiredMessage: this.$route.query.expired !== undefined
        ? "Your session has expired. Request a new sign-in link below."
        : "",
      email: "",
      emailError: "",
      working: false,
      sent: false,
    };
  },
  computed: {
    loadingCaption() {
      return this.$route.query.t ? "Signing you in..." : "Loading...";
    },
  },
  async mounted() {
    const magicToken = this.$route.query.t;
    if (magicToken) {
      const session = await this.$A.PortalService.ExchangeSession(magicToken);
      if (session) {
        // Redirect to the account the session actually belongs to — the URL's
        // account id can be wrong/mangled and must not strand a consumed link.
        this.$router.replace(`/portal/${session.accountPublicId}/proofs`);
        return;
      }
      // The link is spent either way — drop the token from the URL/history.
      const query = Object.assign({}, this.$route.query);
      delete query.t;
      this.$router.replace({ query }).catch(() => {});
      this.expiredMessage = "That sign-in link has expired or was already used. Request a new one below.";
    } else {
      const session = this.$A.PortalService.GetSession(this.accountPublicId);
      if (session) {
        this.$router.replace(`/portal/${this.accountPublicId}/proofs`);
        return;
      }
    }

    this.accountInfo = await this.$A.PortalService.GetAccountInfo(this.accountPublicId);
    if (!this.accountInfo || this.accountInfo.customerPortalEnabled !== true) {
      this.notAvailable = true;
      this.loading = false;
      return;
    }

    if (this.accountInfo.whiteLabelLogoUri) {
      updateFaviconAndTitle({
        accountLogo: this.accountInfo.whiteLabelLogoUri,
        accountTitle: this.accountInfo.name,
      });
    }
    this.loading = false;
  },
  methods: {
    async requestLink() {
      if (this.working) return;
      this.emailError = "";
      const email = this.email.trim();
      if (email.length === 0 || !this.$A.IsEmailValid(email)) {
        this.emailError = "Please enter a valid email address.";
        return;
      }
      this.working = true;
      await this.$A.PortalService.RequestMagicLink(this.accountPublicId, email);
      this.working = false;
      this.sent = true;
    },
  },
};
</script>

<template>
  <auth-shell>
    <div v-if="loading" class="portal-center">
      <b-spinner variant="dark"></b-spinner>
      <p class="auth-subtitle mt-3">{{ loadingCaption }}</p>
    </div>

    <div v-else-if="notAvailable" class="portal-center">
      <h4 class="auth-title">Portal Not Available</h4>
      <p class="auth-subtitle">
        This proof portal isn't available. Please contact the person who sent you
        proofs, or use the review link from your email.
      </p>
    </div>

    <div v-else-if="sent" class="portal-center">
      <div v-if="accountInfo.whiteLabelLogoUri" class="auth-logo">
        <img :src="accountInfo.whiteLabelLogoUri" class="portal-logo" />
      </div>
      <h4 class="auth-title">Check Your Inbox</h4>
      <p class="auth-subtitle">
        If <strong>{{ email }}</strong> has proofs with {{ accountInfo.name }}, a
        sign-in link is on its way. The link works once and expires in 15 minutes.
      </p>
    </div>

    <div v-else>
      <div v-if="accountInfo.whiteLabelLogoUri" class="auth-logo">
        <img :src="accountInfo.whiteLabelLogoUri" class="portal-logo" />
      </div>
      <h4 v-else class="auth-title">{{ accountInfo.name }}</h4>
      <p class="auth-subtitle">
        Enter your email and we'll send you a link to view all of your proofs in
        one place.
      </p>

      <div v-if="expiredMessage" class="alert alert-warning">
        {{ expiredMessage }}
      </div>

      <div class="form-group">
        <label class="auth-label">Your Email</label>
        <input
            type="email"
            name="Email"
            class="auth-field"
            v-model="email"
            v-on:keyup.enter="requestLink()"
        />
        <small v-if="emailError" class="text-danger">{{ emailError }}</small>
      </div>
      <button v-on:click="requestLink()" class="auth-btn-primary mt-3">
        <b-spinner
            variant="light"
            v-show="working"
            type="grow"
            small
        ></b-spinner>
        Email Me A Sign-In Link
      </button>
    </div>
  </auth-shell>
</template>

<style scoped>
.portal-logo {
  max-height: 50px;
  max-width: 180px;
}

.portal-center {
  text-align: center;
  padding: 40px 0;
}
</style>
